/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { LegalDocument, SearchFilters, SearchResult } from '../types';

/**
 * Score weighting:
 * - Match on Reference Number exact/partial: 100 pts (Tier 1)
 * - Match on Title: 50 pts (Tier 2)
 * - Match on Keywords: 20 pts (Tier 3)
 * - Match on Content body: 10 pts (Tier 4)
 */
export function searchAndScoreDocuments(
  documents: LegalDocument[],
  text: string,
  filters: SearchFilters
): SearchResult[] {
  const query = text.trim().toLowerCase();
  const tokens = query.split(/\s+/).filter(t => t.length > 1);

  let filtered = documents;

  // Apply filters first
  if (filters.nature) {
    filtered = filtered.filter(doc => doc.nature === filters.nature);
  }
  if (filters.legalOrigin) {
    filtered = filtered.filter(doc => doc.legalOrigin === filters.legalOrigin);
  }
  if (filters.legalStatus) {
    filtered = filtered.filter(doc => doc.legalStatus === filters.legalStatus);
  }
  if (filters.areaOfLaw) {
    filtered = filtered.filter(doc => doc.areaOfLaw === filters.areaOfLaw);
  }
  if (filters.issuingAuthority) {
    const authQuery = filters.issuingAuthority.toLowerCase();
    filtered = filtered.filter(doc => doc.issuingAuthority?.toLowerCase().includes(authQuery));
  }
  if (filters.effectiveYear) {
    filtered = filtered.filter(doc => doc.effectiveYear === filters.effectiveYear);
  }
  if (filters.keywords && filters.keywords.length > 0) {
    filtered = filtered.filter(doc => 
      filters.keywords?.every(kw => 
        doc.legalKeywords.some(docKw => docKw.toLowerCase() === kw.toLowerCase())
      )
    );
  }

  // If search query is empty, return everything with base score & empty highlights
  if (!query) {
    return filtered.map(doc => ({
      document: doc,
      score: 1,
      highlight: doc.officialTitle.slice(0, 160) + "...",
      matchedField: 'TITLE'
    }));
  }

  const results: SearchResult[] = [];

  for (const doc of filtered) {
    let score = 0;
    let matchedField: 'REFERENCE' | 'TITLE' | 'KEYWORD' | 'CONTENT' = 'CONTENT';
    let highlight = "";

    const refLower = doc.referenceNumber.toLowerCase();
    const titleLower = doc.officialTitle.toLowerCase();
    const fullText = doc.fullTextMarkdown || "";
    const contentLower = fullText.toLowerCase();

    // Section Reference Numbers Tier 1
    if (refLower === query) {
      score += 1000; // Exact reference match is top priority
      matchedField = 'REFERENCE';
    } else if (refLower.includes(query)) {
      score += 500;
      matchedField = 'REFERENCE';
    } else {
      const refTokenMatches = tokens.filter(t => refLower.includes(t));
      score += refTokenMatches.length * 20;
    }

    // Section Titles Tier 2
    if (titleLower === query) {
      score += 1000;
      matchedField = 'TITLE';
    } else if (titleLower.startsWith(query)) {
      score += 800;
      matchedField = 'TITLE';
    } else if (titleLower.includes(query)) {
      score += 500;
      matchedField = 'TITLE';
    } else {
      // Check individual tokens
      const tokenMatches = tokens.filter(t => titleLower.includes(t));
      score += tokenMatches.length * 10; // Keep this low enough so it never beats an exact phrase match
      if (tokenMatches.length > 0 && matchedField === 'CONTENT') matchedField = 'TITLE';
    }

    // Keywords Tier 3
    const exactKeyword = doc.legalKeywords.some(kw => kw.toLowerCase() === query);
    if (exactKeyword) {
      score += 300;
      if (matchedField === 'CONTENT') matchedField = 'KEYWORD';
    } else {
      const keywordMatches = doc.legalKeywords.filter(kw => kw.toLowerCase().includes(query));
      if (keywordMatches.length > 0) {
        score += 50;
        if (matchedField === 'CONTENT') matchedField = 'KEYWORD';
      }
    }

    // Content body Tier 4
    if (contentLower.includes(query)) {
      score += 30; // Better than single tokens, worse than title phrase
      if (matchedField === 'CONTENT') matchedField = 'CONTENT';
      
      // Extract brief paragraph matching study term
      const index = contentLower.indexOf(query);
      const start = Math.max(0, index - 60);
      const end = Math.min(fullText.length, index + query.length + 80);
      highlight = "..." + fullText.slice(start, end).replace(/\n/g, " ") + "...";
    } else {
      // Look for individual tokens in content
      const contentTokenMatches = tokens.filter(t => contentLower.includes(t));
      score += contentTokenMatches.length * 2;

      if (contentTokenMatches.length > 0 && score > 0) {
        const firstToken = contentTokenMatches[0];
        const index = contentLower.indexOf(firstToken);
        const start = Math.max(0, index - 60);
        const end = Math.min(fullText.length, index + firstToken.length + 80);
        highlight = "..." + fullText.slice(start, end).replace(/\n/g, " ") + "...";
      }
    }

    if (score > 0) {
      const displayHighlight = highlight || (doc.officialTitle.length > 150 ? doc.officialTitle.slice(0, 150) + "..." : doc.officialTitle);
      results.push({
        document: doc,
        score,
        highlight: displayHighlight,
        matchedField
      });
    }
  }

  // Sort according to priority defined in the blueprint (Section 10):
  // 1. Score de pertinence (desc)
  // 2. Statut (EN_VIGUEUR > MODIFIE > SUSPENDU > ABROGE > REMPLACE)
  // 3. Date d'adoption desc (nouvel avant)
  return results.sort((a, b) => {
    if (b.score !== a.score) {
      return b.score - a.score;
    }
    
    const statusOrder: Record<string, number> = { 
      EnVigeur: 0, 
      Modifie: 1, 
      Suspendu: 2, 
      Abroge: 3, 
      Remplace: 4 
    };
    
    const statusA = statusOrder[a.document.legalStatus] ?? 99;
    const statusB = statusOrder[b.document.legalStatus] ?? 99;
    
    if (statusA !== statusB) {
      return statusA - statusB; // Lower number is higher status priority
    }
    
    const dateA = new Date(a.document.dateOfAdoption).getTime();
    const dateB = new Date(b.document.dateOfAdoption).getTime();
    return dateB - dateA;
  });
}

/**
 * Helper to display pretty dates in French
 */
export function formatFrenchDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const months = [
      "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
      "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
    ];
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  } catch (e) {
    return dateStr;
  }
}

export interface HumanDateTimeInfo {
  /** ex: "Aujourd'hui à 14:35", "Hier à 09:20", "Lundi à 16:45", "12 oct. 2026 à 11:00" */
  label: string;
  /** ex: "Il y a 5 min", "Il y a 2 h", "Il y a 3 j", "À l'instant" */
  relative: string;
  /** ex: "14:35" */
  time: string;
  /** ex: "14:35:22" */
  timeWithSeconds: string;
  /** Date/time formatted with seconds: "Aujourd'hui à 14:35:22" */
  labelWithSeconds: string;
  /** Full localized string for native tooltip: "mercredi 28 octobre 2026 à 14:35:22" */
  full: string;
  /** Type of period for specific badge/color coding: 'today' | 'yesterday' | 'week' | 'older' */
  period: 'today' | 'yesterday' | 'week' | 'older';
}

/**
 * Format a timestamp into an intuitive human-friendly French representation:
 * - "Aujourd'hui à 14:35"
 * - "Hier à 09:15"
 * - "Lundi à 18:20" (within last 7 days)
 * - "12 oct. à 10:45" (current year)
 * - "15 nov. 2024 à 10:45" (past years)
 * Along with relative time ("Il y a 5 min", "À l'instant") and exact seconds.
 */
export function formatHumanDateTime(dateInput?: string | Date | number | null): HumanDateTimeInfo {
  if (!dateInput) {
    return {
      label: "Date inconnue",
      relative: "",
      time: "",
      timeWithSeconds: "",
      labelWithSeconds: "Date inconnue",
      full: "",
      period: 'older'
    };
  }

  const date = typeof dateInput === 'object' && dateInput instanceof Date ? dateInput : new Date(dateInput);

  if (isNaN(date.getTime())) {
    return {
      label: String(dateInput),
      relative: "",
      time: "",
      timeWithSeconds: "",
      labelWithSeconds: String(dateInput),
      full: String(dateInput),
      period: 'older'
    };
  }

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);

  const time = date.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit'
  });

  const timeWithSeconds = date.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });

  const full = date.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }) + ` à ${timeWithSeconds}`;

  // Helper to compare calendar days in local time
  const isSameDay = (d1: Date, d2: Date) =>
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);

  // Relative calculation
  let relative = "";
  if (diffSec < 45) {
    relative = "À l'instant";
  } else if (diffSec < 3600) {
    const mins = Math.max(1, Math.floor(diffSec / 60));
    relative = `Il y a ${mins} min`;
  } else if (diffSec < 86400 && isSameDay(date, now)) {
    const hours = Math.floor(diffSec / 3600);
    relative = `Il y a ${hours} h`;
  } else if (isSameDay(date, yesterday)) {
    relative = "Hier";
  } else if (diffSec < 86400 * 7) {
    const days = Math.max(1, Math.floor(diffSec / 86400));
    relative = `Il y a ${days} j`;
  } else if (diffSec < 86400 * 30) {
    const weeks = Math.floor(diffSec / (86400 * 7));
    relative = `Il y a ${weeks} sem.`;
  } else if (diffSec < 86400 * 365) {
    const months = Math.floor(diffSec / (86400 * 30));
    relative = `Il y a ${months} mois`;
  } else {
    const years = Math.floor(diffSec / (86400 * 365));
    relative = `Il y a ${years} an${years > 1 ? 's' : ''}`;
  }

  // Today
  if (isSameDay(date, now)) {
    return {
      label: `Aujourd'hui à ${time}`,
      relative,
      time,
      timeWithSeconds,
      labelWithSeconds: `Aujourd'hui à ${timeWithSeconds}`,
      full,
      period: 'today'
    };
  }

  // Yesterday
  if (isSameDay(date, yesterday)) {
    return {
      label: `Hier à ${time}`,
      relative: relative || "Hier",
      time,
      timeWithSeconds,
      labelWithSeconds: `Hier à ${timeWithSeconds}`,
      full,
      period: 'yesterday'
    };
  }

  // Last 6 days (display weekday name, e.g. "Lundi à 14:30")
  const startOfNow = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const daysDiff = Math.round((startOfNow - startOfDate) / (1000 * 60 * 60 * 24));

  if (daysDiff > 1 && daysDiff < 7) {
    const weekday = date.toLocaleDateString('fr-FR', { weekday: 'long' });
    const capitalizedWeekday = weekday.charAt(0).toUpperCase() + weekday.slice(1);
    return {
      label: `${capitalizedWeekday} à ${time}`,
      relative,
      time,
      timeWithSeconds,
      labelWithSeconds: `${capitalizedWeekday} à ${timeWithSeconds}`,
      full,
      period: 'week'
    };
  }

  // Same year vs past/future year
  const isSameYear = date.getFullYear() === now.getFullYear();
  const dateFormatted = date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    ...(isSameYear ? {} : { year: 'numeric' })
  });

  return {
    label: `${dateFormatted} à ${time}`,
    relative,
    time,
    timeWithSeconds,
    labelWithSeconds: `${dateFormatted} à ${timeWithSeconds}`,
    full,
    period: 'older'
  };
}


/**
 * Map Domain enums to nice French strings
 */
export function formatDomain(domain: string | null): string {
  if (!domain) return "Non classé";
  const mapping: Record<string, string> = {
    DROIT_CIVIL: "Droit Civil",
    DROIT_PENAL: "Droit Pénal",
    DROIT_COMMERCIAL: "Droit Commercial",
    DROIT_FISCAL: "Droit Fiscal",
    DROIT_SOCIAL: "Droit Social",
    DROIT_ADMINISTRATIF: "Droit Administratif",
    DROIT_CONSTITUTIONNEL: "Droit Constitutionnel",
    DROIT_FONCIER: "Droit Foncier",
    DROIT_MINIER: "Droit Minier",
    DROIT_ENVIRONNEMENT: "Droit de l'Environnement",
    DROIT_DOUANIER: "Droit Douanier",
    DROIT_BANQUE: "Droit Bancaire",
    DROIT_OHADA: "Droit des Affaires (OHADA)",
    DROIT_INTERNATIONAL: "Droit International",
    DROIT_TRAVAIL: "Droit du Travail"
  };
  return mapping[domain] || domain.replace(/_/g, " ");
}

/**
 * Map Nature enums to nice French strings
 */
export function formatNature(nature: string): string {
  const mapping: Record<string, string> = {
    CONSTITUTION: "Constitution",
    LOI: "Loi",
    ORDONNANCE: "Ordonnance",
    DECRET: "Décret",
    ARRETE: "Arrêté",
    CIRCULAIRE: "Circulaire",
    DECISION: "Décision",
    NOTE_DE_SERVICE: "Note de Service",
    TRAITE: "Traité",
    CONVENTION: "Convention",
    ACCORD: "Accord",
    REGLEMENT: "Règlement",
    DIRECTIVE: "Directive",
    JO_CAMEROUN: "Journal Officiel",
    OHADA: "Acte Unique OHADA",
    CEMAC: "Règlement CEMAC",
    COBAC: "Règlement COBAC"
  };
  return mapping[nature] || nature;
}
